import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useParams, Link } from "react-router-dom";

import { Avatar, PriorityBadge, TicketKey } from "@m2s/design-system";

import { CreateIssueModal } from "@/components/CreateIssueModal";
import { DesktopOnly } from "@/components/DesktopOnly";
import { initialsOf, useDirectory } from "@/hooks/useDirectory";
import { api } from "@/lib/api";
import type { DirectoryMember, Issue, Project, Sprint } from "@/lib/types";

const COLUMNS: { key: Issue["status"]; label: string; accent: string }[] = [
  { key: "todo", label: "To do", accent: "var(--mute)" },
  { key: "in_progress", label: "In progress", accent: "#7a3dff" },
  { key: "in_review", label: "In review", accent: "#146ef5" },
  { key: "done", label: "Done", accent: "#00d722" },
];

const STATUS_META: Record<Issue["status"], { label: string; accent: string }> = {
  todo: { label: "To do", accent: "var(--mute)" },
  in_progress: { label: "In progress", accent: "#7a3dff" },
  in_review: { label: "In review", accent: "#146ef5" },
  done: { label: "Done", accent: "#00d722" },
  blocked: { label: "Blocked", accent: "#e5484d" },
};

const COLLAPSE_KEY = "m2s.board.collapsed";
const UNASSIGNED = "__unassigned__";

function readCollapsed(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function writeCollapsed(state: Record<string, boolean>) {
  try {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(state));
  } catch {
    /* noop */
  }
}

export function BoardPage() {
  // Drag-and-drop on the kanban needs a touch-aware DnD library before
  // this view is usable on a phone; until then show the "open on
  // desktop" notice below 1024px. The Impl below only renders on wider
  // viewports, so all the queries + state are skipped on mobile too.
  return (
    <DesktopOnly
      title="The board"
      blurb="The kanban needs a wider canvas — dragging tasks between columns isn't usable at phone widths yet."
    >
      <BoardPageImpl />
    </DesktopOnly>
  );
}

function BoardPageImpl() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const queryClient = useQueryClient();
  const isAllBoards = !projectKey;
  const key = (projectKey ?? "").toUpperCase();
  const [createOpen, setCreateOpen] = useState(false);
  const [createDefaultKey, setCreateDefaultKey] = useState<string | undefined>(undefined);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => readCollapsed());
  const [view, setView] = useState<"board" | "people">("board");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all"); // "all" | UNASSIGNED | user_id

  useEffect(() => writeCollapsed(collapsed), [collapsed]);

  const toggleProject = (k: string) =>
    setCollapsed((prev) => ({ ...prev, [k]: !prev[k] }));

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get<Project[]>("/projects")).data,
  });
  const projectByKey = new Map(projects.map((p) => [p.key, p]));
  const projectById = new Map(projects.map((p) => [p.id, p]));
  const project = isAllBoards ? null : projectByKey.get(key);

  const { data: sprints } = useQuery({
    queryKey: ["sprints", key],
    queryFn: async () => (await api.get<Sprint[]>(`/projects/${key}/sprints`)).data,
    enabled: !isAllBoards,
  });
  const activeSprint = sprints?.find((s) => s.state === "active") ?? null;

  const { data: issues, isLoading } = useQuery({
    queryKey: isAllBoards ? ["issues", "all"] : ["issues", key],
    queryFn: async () =>
      (
        await api.get<Issue[]>(
          isAllBoards ? "/issues?limit=500" : `/issues?project=${key}&limit=200`,
        )
      ).data,
  });

  // Name-only roster — readable by employees too, so avatars/names resolve for
  // everyone (the old `/admin/members` fetch 403'd for non-admins).
  const { byId: memberById } = useDirectory();

  const transition = useMutation({
    mutationFn: async ({ key: ikey, status }: { key: string; status: Issue["status"] }) =>
      (await api.post(`/issues/${ikey}/transition`, { status })).data,
    onMutate: async ({ key: ikey, status }) => {
      // Optimistically flip status across every cached board list so the drop feels instant.
      const queries = queryClient.getQueriesData<Issue[]>({ queryKey: ["issues"] });
      const snapshot = queries.map(([key, data]) => [key, data] as const);
      for (const [qk, data] of queries) {
        if (!data) continue;
        queryClient.setQueryData<Issue[]>(
          qk,
          data.map((i) => (i.key === ikey ? { ...i, status } : i)),
        );
      }
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back if the server rejected the transition.
      if (!ctx) return;
      for (const [qk, data] of ctx.snapshot) {
        queryClient.setQueryData(qk, data);
      }
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ["issues"] });
      queryClient.invalidateQueries({ queryKey: ["issue", vars.key] });
    },
  });

  const openCreate = (defaultKey?: string) => {
    setCreateDefaultKey(defaultKey);
    setCreateOpen(true);
  };

  const allIssues = issues ?? [];

  // Assignee facets present in the current issue set (drives the filter dropdown).
  const assigneeFacets = useMemo(() => {
    const counts = new Map<string, number>();
    let unassigned = 0;
    for (const i of allIssues) {
      if (i.assignee_id) counts.set(i.assignee_id, (counts.get(i.assignee_id) ?? 0) + 1);
      else unassigned += 1;
    }
    const people = [...counts.entries()]
      .map(([uid, n]) => ({ uid, n, name: memberById.get(uid)?.display_name ?? "Unknown" }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { people, unassigned };
  }, [allIssues, memberById]);

  // Board view honours the assignee filter; By-person view shows everyone.
  const matchesAssignee = (i: Issue) =>
    assigneeFilter === "all" ||
    (assigneeFilter === UNASSIGNED ? !i.assignee_id : i.assignee_id === assigneeFilter);
  const boardIssues = allIssues.filter(matchesAssignee);

  // Group (filtered) issues by project id for the all-boards view.
  const issuesByProject = new Map<string, Issue[]>();
  for (const i of boardIssues) {
    const arr = issuesByProject.get(i.project_id) ?? [];
    arr.push(i);
    issuesByProject.set(i.project_id, arr);
  }

  const totalPoints = boardIssues.reduce((acc, i) => acc + (i.estimate_points ?? 0), 0);
  const donePoints = boardIssues
    .filter((i) => i.status === "done")
    .reduce((acc, i) => acc + (i.estimate_points ?? 0), 0);

  const onTransition = (k: string, status: Issue["status"]) =>
    transition.mutate({ key: k, status });

  return (
    <div>
      <header style={{ marginBottom: 20, display: "flex", alignItems: "baseline", gap: 16 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          {isAllBoards ? "All boards" : `${project?.name ?? key} board`}
        </h1>
        {isAllBoards ? (
          <span style={{ fontSize: 13, color: "var(--mute)" }}>
            {boardIssues.length} issue{boardIssues.length === 1 ? "" : "s"} across{" "}
            {projects.length} project{projects.length === 1 ? "" : "s"}
            {totalPoints > 0 && ` · ${donePoints} / ${totalPoints} points done`}
          </span>
        ) : (
          activeSprint && (
            <span style={{ fontSize: 13, color: "var(--mute)" }}>
              {activeSprint.name} · {donePoints} / {totalPoints} points
            </span>
          )
        )}
      </header>

      {/* Toolbar: view toggle + assignee filter + expand/collapse. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "inline-flex", border: "1px solid var(--hairline)", borderRadius: 4, overflow: "hidden" }}>
          <ToggleBtn active={view === "board"} onClick={() => setView("board")}>
            Board
          </ToggleBtn>
          <ToggleBtn active={view === "people"} onClick={() => setView("people")}>
            By person
          </ToggleBtn>
        </div>

        {view === "board" && (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--mute)" }}>
            Assignee
            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              style={{
                background: "var(--canvas)",
                border: "1px solid var(--hairline)",
                borderRadius: 4,
                fontSize: 12,
                color: "var(--ink)",
                padding: "5px 8px",
                cursor: "pointer",
              }}
            >
              <option value="all">Everyone ({allIssues.length})</option>
              {assigneeFacets.unassigned > 0 && (
                <option value={UNASSIGNED}>Unassigned ({assigneeFacets.unassigned})</option>
              )}
              {assigneeFacets.people.map((p) => (
                <option key={p.uid} value={p.uid}>
                  {p.name} ({p.n})
                </option>
              ))}
            </select>
          </label>
        )}

        {view === "board" && isAllBoards && projects.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const allCollapsed = projects.every((p) => collapsed[p.key]);
              const next: Record<string, boolean> = {};
              for (const p of projects) next[p.key] = !allCollapsed;
              setCollapsed(next);
            }}
            style={{ ...toolbarBtn, marginLeft: "auto" }}
          >
            {projects.every((p) => collapsed[p.key]) ? "Expand all" : "Collapse all"}
          </button>
        )}
      </div>

      {isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : view === "people" ? (
        <PeopleView
          issues={allIssues}
          memberById={memberById}
          projectById={projectById}
          showProject={isAllBoards}
        />
      ) : isAllBoards ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {projects.length === 0 && (
            <p style={{ color: "var(--mute)" }}>No projects yet.</p>
          )}
          {projects.map((p) => {
            const projIssues = issuesByProject.get(p.id) ?? [];
            const projTotal = projIssues.reduce((a, i) => a + (i.estimate_points ?? 0), 0);
            const projDone = projIssues
              .filter((i) => i.status === "done")
              .reduce((a, i) => a + (i.estimate_points ?? 0), 0);
            const isCollapsed = !!collapsed[p.key];
            // When filtering by assignee, hide projects with no matching tickets.
            if (assigneeFilter !== "all" && projIssues.length === 0) return null;
            return (
              <ProjectSection
                key={p.id}
                project={p}
                collapsed={isCollapsed}
                onToggle={() => toggleProject(p.key)}
                issueCount={projIssues.length}
                donePoints={projDone}
                totalPoints={projTotal}
                onCreate={() => openCreate(p.key)}
              >
                {!isCollapsed && (
                  <BoardColumns
                    issues={projIssues}
                    memberById={memberById}
                    onTransition={onTransition}
                  />
                )}
              </ProjectSection>
            );
          })}
        </div>
      ) : (
        <BoardColumns
          issues={boardIssues}
          memberById={memberById}
          onTransition={onTransition}
        />
      )}

      {view === "board" && !isAllBoards && (
        <button type="button" onClick={() => openCreate(key)} style={createBtn}>
          + Create task
        </button>
      )}

      <CreateIssueModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultProjectKey={createDefaultKey}
      />
    </div>
  );
}

/* ---------- By-person view: who owns what, who finished what ---------- */

function PeopleView({
  issues,
  memberById,
  projectById,
  showProject,
}: {
  issues: Issue[];
  memberById: Map<string, DirectoryMember>;
  projectById: Map<string, Project>;
  showProject: boolean;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Bucket issues per assignee (+ an Unassigned bucket).
  const buckets = useMemo(() => {
    const map = new Map<string, Issue[]>();
    for (const i of issues) {
      const k = i.assignee_id ?? UNASSIGNED;
      const arr = map.get(k) ?? [];
      arr.push(i);
      map.set(k, arr);
    }
    const rows = [...map.entries()].map(([k, list]) => {
      const done = list.filter((i) => i.status === "done").length;
      return {
        key: k,
        name: k === UNASSIGNED ? "Unassigned" : memberById.get(k)?.display_name ?? "Unknown",
        role: k === UNASSIGNED ? null : memberById.get(k)?.role ?? null,
        list: [...list].sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true })),
        done,
        total: list.length,
      };
    });
    // People first (alphabetical), Unassigned last.
    rows.sort((a, b) => {
      if (a.key === UNASSIGNED) return 1;
      if (b.key === UNASSIGNED) return -1;
      return a.name.localeCompare(b.name);
    });
    return rows;
  }, [issues, memberById]);

  if (issues.length === 0) {
    return <p style={{ color: "var(--mute)" }}>No tasks yet.</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {buckets.map((row) => {
        const isOpen = expanded[row.key] ?? true;
        const counts = COLUMNS.map((c) => ({
          ...c,
          n: row.list.filter((i) => i.status === c.key).length,
        }));
        const blocked = row.list.filter((i) => i.status === "blocked").length;
        const pct = row.total ? Math.round((row.done / row.total) * 100) : 0;
        return (
          <section
            key={row.key}
            style={{
              border: "1px solid var(--hairline)",
              borderRadius: 8,
              background: "var(--canvas)",
              overflow: "hidden",
            }}
          >
            <header
              onClick={() => setExpanded((p) => ({ ...p, [row.key]: !isOpen }))}
              role="button"
              aria-expanded={isOpen}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                background: "var(--surface-1)",
                borderBottom: isOpen ? "1px solid var(--hairline)" : "none",
                cursor: "pointer",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 10,
                  color: "var(--mute)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  transition: "transform 120ms",
                  transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                }}
              >
                ▾
              </span>
              {row.key === UNASSIGNED ? (
                <Avatar initials="—" title="Unassigned" size={24} />
              ) : (
                <Avatar initials={initialsOf(row.name)} title={row.name} size={24} />
              )}
              <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.1px", color: "var(--ink)" }}>
                {row.name}
              </span>
              {row.role === "admin" && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: "var(--body-mid)",
                    background: "var(--surface-2)",
                    padding: "1px 6px",
                    borderRadius: 3,
                  }}
                >
                  Admin
                </span>
              )}

              <span style={{ display: "inline-flex", gap: 6, alignItems: "center", marginLeft: 8 }}>
                {counts.map((c) => (
                  <span
                    key={c.key}
                    title={c.label}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: "var(--mute)" }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: 9999, background: c.accent }} />
                    {c.label} {c.n}
                  </span>
                ))}
                {blocked > 0 && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: "var(--mute)" }}>
                    <span style={{ width: 6, height: 6, borderRadius: 9999, background: STATUS_META.blocked.accent }} />
                    Blocked {blocked}
                  </span>
                )}
              </span>

              <span style={{ flex: 1 }} />

              {/* Progress: done / total + a slim bar. */}
              <span style={{ fontSize: 12, color: "var(--mute)", fontFamily: "var(--font-mono)" }}>
                {row.done}/{row.total} done
              </span>
              <span
                aria-hidden
                style={{ width: 90, height: 6, borderRadius: 9999, background: "var(--surface-2)", overflow: "hidden" }}
              >
                <span style={{ display: "block", height: "100%", width: `${pct}%`, background: "#00d722" }} />
              </span>
            </header>

            {isOpen && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {row.list.map((i) => {
                  const meta = STATUS_META[i.status];
                  const proj = projectById.get(i.project_id);
                  return (
                    <Link
                      key={i.id}
                      to={`/issues/${i.key}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "9px 16px",
                        textDecoration: "none",
                        color: "var(--ink)",
                        borderTop: "1px solid var(--hairline)",
                      }}
                    >
                      <TicketKey value={i.key} />
                      <PriorityBadge priority={i.priority} />
                      <span style={{ fontSize: 13.5, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {i.title}
                      </span>
                      {showProject && proj && (
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 10.5,
                            color: "var(--mute)",
                            background: "var(--surface-2)",
                            padding: "1px 6px",
                            borderRadius: 3,
                          }}
                        >
                          {proj.key}
                        </span>
                      )}
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--body-mid)", minWidth: 96, justifyContent: "flex-end" }}>
                        <span style={{ width: 7, height: 7, borderRadius: 9999, background: meta.accent }} />
                        {meta.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function ToggleBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? "var(--ink)" : "var(--canvas)",
        color: active ? "var(--on-primary)" : "var(--body-mid)",
        border: "none",
        padding: "6px 14px",
        fontSize: 12.5,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

/* ---------- Collapsible project section ---------- */

function ProjectSection({
  project,
  collapsed,
  onToggle,
  issueCount,
  donePoints,
  totalPoints,
  onCreate,
  children,
}: {
  project: Project;
  collapsed: boolean;
  onToggle: () => void;
  issueCount: number;
  donePoints: number;
  totalPoints: number;
  onCreate: () => void;
  children?: React.ReactNode;
}) {
  return (
    <section
      style={{
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        background: "var(--canvas)",
        overflow: "hidden",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 16px",
          background: "var(--surface-1)",
          borderBottom: collapsed ? "none" : "1px solid var(--hairline)",
          cursor: "pointer",
        }}
        onClick={onToggle}
        role="button"
        aria-expanded={!collapsed}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 10,
            color: "var(--mute)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            transition: "transform 120ms",
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
          }}
        >
          ▾
        </span>
        <span
          aria-hidden
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            background: project.color,
            flexShrink: 0,
          }}
        />
        <Link
          to={`/board/${project.key}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: "-0.1px",
            color: "var(--ink)",
            textDecoration: "none",
          }}
        >
          {project.name}
        </Link>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--mute)",
            background: "var(--surface-2)",
            padding: "1px 6px",
            borderRadius: 3,
          }}
        >
          {project.key}
        </span>
        <span style={{ fontSize: 12, color: "var(--mute)", marginLeft: 8 }}>
          {issueCount} issue{issueCount === 1 ? "" : "s"}
          {totalPoints > 0 && ` · ${donePoints}/${totalPoints} pts`}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCreate();
          }}
          style={ghostBtn}
        >
          + New task
        </button>
        <Link
          to={`/board/${project.key}`}
          onClick={(e) => e.stopPropagation()}
          style={ghostLink}
        >
          Open →
        </Link>
      </header>
      {children && <div style={{ padding: 12 }}>{children}</div>}
    </section>
  );
}

/* ---------- 4-column kanban (shared by single + per-project views) ---------- */

function BoardColumns({
  issues,
  memberById,
  onTransition,
}: {
  issues: Issue[];
  memberById: Map<string, DirectoryMember>;
  onTransition: (issueKey: string, status: Issue["status"]) => void;
}) {
  // Track drag state for visual affordances (which column is hovered, which card is grabbed).
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [hoverColumn, setHoverColumn] = useState<Issue["status"] | null>(null);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 12,
      }}
    >
      {COLUMNS.map((col) => {
        const colIssues = issues.filter((i) => i.status === col.key);
        const isDropTarget = hoverColumn === col.key && draggingKey !== null;
        return (
          <section
            key={col.key}
            onDragOver={(e) => {
              // Required to allow drop. Setting effect to "move" hints the OS cursor.
              e.preventDefault();
              if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
              if (hoverColumn !== col.key) setHoverColumn(col.key);
            }}
            onDragLeave={(e) => {
              // Only clear if leaving the column itself, not entering a child.
              if (e.currentTarget.contains(e.relatedTarget as Node)) return;
              if (hoverColumn === col.key) setHoverColumn(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              const ikey = e.dataTransfer?.getData("text/plain");
              setHoverColumn(null);
              setDraggingKey(null);
              if (!ikey) return;
              const dragged = issues.find((i) => i.key === ikey);
              if (!dragged || dragged.status === col.key) return;
              onTransition(ikey, col.key);
            }}
            style={{
              background: isDropTarget ? "rgba(122,61,255,0.06)" : "var(--surface-1)",
              border: isDropTarget
                ? "1px dashed var(--accent-purple, #7a3dff)"
                : "1px solid var(--hairline)",
              borderRadius: 6,
              padding: 8,
              minHeight: 120,
              transition: "background 120ms, border-color 120ms",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 8px 10px",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 9999,
                  background: col.accent,
                }}
              />
              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--ink)" }}>
                {col.label}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--mute)",
                  marginLeft: "auto",
                }}
              >
                {colIssues.length}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {colIssues.map((issue) => {
                const assignee = issue.assignee_id ? memberById.get(issue.assignee_id) : null;
                const isDragging = draggingKey === issue.key;
                return (
                  <div
                    key={issue.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", issue.key);
                      e.dataTransfer.effectAllowed = "move";
                      setDraggingKey(issue.key);
                    }}
                    onDragEnd={() => {
                      setDraggingKey(null);
                      setHoverColumn(null);
                    }}
                    style={{
                      padding: 10,
                      background: "var(--canvas)",
                      border: "1px solid var(--hairline)",
                      borderRadius: 4,
                      cursor: isDragging ? "grabbing" : "grab",
                      opacity: isDragging ? 0.4 : 1,
                      boxShadow: isDragging
                        ? "0 12px 24px rgba(0,0,0,0.10), 0 4px 8px rgba(0,0,0,0.06)"
                        : undefined,
                      transition: "opacity 120ms, box-shadow 120ms",
                    }}
                  >
                    <Link
                      to={`/issues/${issue.key}`}
                      draggable={false}
                      style={{
                        display: "block",
                        textDecoration: "none",
                        color: "var(--ink)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <TicketKey value={issue.key} />
                        <PriorityBadge priority={issue.priority} />
                        <span
                          style={{
                            marginLeft: "auto",
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            color: "var(--mute)",
                          }}
                        >
                          {issue.estimate_points ?? "—"}pt
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 13.5,
                          margin: "8px 0 6px",
                          lineHeight: 1.35,
                        }}
                      >
                        {issue.title}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {issue.labels.slice(0, 2).map((lbl) => (
                          <span
                            key={lbl}
                            style={{
                              fontSize: 10,
                              padding: "1px 6px",
                              background: "var(--surface-2)",
                              borderRadius: 2,
                              color: "var(--body-mid)",
                            }}
                          >
                            {lbl}
                          </span>
                        ))}
                        {/* Assignee: avatar + first name so ownership reads at a glance. */}
                        {assignee ? (
                          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 11, color: "var(--mute)" }}>
                              {assignee.display_name.split(/\s+/)[0]}
                            </span>
                            <Avatar
                              initials={initialsOf(assignee.display_name)}
                              title={assignee.display_name}
                              size={20}
                            />
                          </span>
                        ) : (
                          <span style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--mute)" }}>
                            Unassigned
                          </span>
                        )}
                      </div>
                    </Link>
                    <select
                      aria-label="Change status"
                      value={issue.status}
                      onChange={(e) => onTransition(issue.key, e.target.value as Issue["status"])}
                      style={{
                        marginTop: 8,
                        width: "100%",
                        background: "var(--surface-1)",
                        border: "1px solid var(--hairline)",
                        borderRadius: 3,
                        fontSize: 11,
                        color: "var(--body-mid)",
                        padding: "3px 4px",
                        cursor: "pointer",
                      }}
                    >
                      {COLUMNS.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                      <option value="blocked">Blocked</option>
                    </select>
                  </div>
                );
              })}
              {colIssues.length === 0 && (
                <div
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    color: isDropTarget ? "var(--accent-purple, #7a3dff)" : "var(--mute)",
                    fontSize: 11.5,
                    border: "1px dashed var(--hairline)",
                    borderRadius: 4,
                    fontWeight: isDropTarget ? 500 : 400,
                  }}
                >
                  {isDropTarget ? "Drop here" : "No tasks."}
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

const createBtn: CSSProperties = {
  marginTop: 16,
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const toolbarBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "5px 10px",
  fontSize: 12,
  color: "var(--ink)",
  cursor: "pointer",
};

const ghostBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "4px 10px",
  fontSize: 12,
  color: "var(--ink)",
  cursor: "pointer",
  fontWeight: 500,
};

const ghostLink: CSSProperties = {
  fontSize: 12,
  color: "var(--body-mid)",
  textDecoration: "none",
  padding: "4px 8px",
  fontWeight: 500,
};
